import React, { useMemo, useRef, useState } from 'react';

import { isUUIDV4 } from '@aline/common';
import type { UUID } from '@aline/common';
import { getFileFieldDisplayName } from '@aline/graphql';
import type { PopperProps } from '@mui/material';
import { Divider, MenuItem, MenuList, Paper, Popper, Typography } from '@mui/material';
import type { GridApiPremium } from '@mui/x-data-grid-premium';
import { matchSorter } from 'match-sorter';
import { uniq } from 'ramda';
import { useClickAway } from 'react-use';
import { AlineCheckbox } from 'src/components/generic';
import { GenericPropertyIcon } from 'src/components/icons/GenericPropertyIcon';
import { SmallAiSparkleIcon } from 'src/components/icons/SmallAiSparkleIcon';
import type { ColumnHeaderQuestion } from 'src/components/reports/report-detail/ColumnHeaderMenu';
import { SearchInput } from 'src/components/SearchInput';
import { Apollo } from 'src/gql/generated';
import { domElIdCreators } from 'src/helpers';
import { useGetAllExtractionQuestions } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

export const columnMenuPopperWidthPx = 300;
const useStyles = makeStyles()(theme => ({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 440,
    width: columnMenuPopperWidthPx,
  },
  popper: {
    zIndex: theme.zIndex.modal,
  },
  scrollContainer: {
    overflowY: 'auto',
  },
  searchContainer: {
    padding: theme.spacing(1, 2),
  },
}));

interface SelectedInputs {
  inputFileFields: Apollo.ReportColumnFileField[];
  inputQuestionIds: UUID[];
}

interface ExtractionQuestionInputsPopperProps {
  apiRef: React.RefObject<GridApiPremium | null> | null;
  inputFileFields: Apollo.ReportColumnFileField[];
  inputQuestionIds: UUID[];
  onChangeSelectedInputs: (inputs: SelectedInputs) => void;
  onClose: () => void;
  popperProps: Omit<PopperProps, 'children'>;
  question: ColumnHeaderQuestion;
}

type CompatibleColumn =
  | { field: string; variant: 'ai'; headerName?: string; type?: string }
  | {
      field: Apollo.ReportColumnFileField;
      variant: 'fileField';
      headerName?: string;
      type?: string;
    };

function toFileFieldColumn(field: Apollo.ReportColumnFileField): CompatibleColumn {
  return {
    field,
    headerName: getFileFieldDisplayName(field),
    type: 'string',
    variant: 'fileField',
  };
}

export function ExtractionQuestionInputsPopper({
  apiRef,
  inputFileFields,
  inputQuestionIds,
  onChangeSelectedInputs,
  onClose,
  popperProps: { anchorEl, placement, ...rest },
  question,
}: ExtractionQuestionInputsPopperProps): JSX.Element {
  const { classes } = useStyles();
  const popperRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState<string>('');
  const { extractionQuestions } = useGetAllExtractionQuestions();

  const allFileFieldValues = useMemo(() => Object.values(Apollo.ReportColumnFileField), []);

  const { selectedInputs, unselectedInputs } = useMemo(() => {
    // Build file field items
    const selectedFileFields = allFileFieldValues
      .filter(field => inputFileFields.includes(field))
      .map(toFileFieldColumn);
    const unselectedFileFields = allFileFieldValues
      .filter(field => !inputFileFields.includes(field))
      .map(toFileFieldColumn);

    if (!apiRef || !apiRef.current) {
      const selectedAi = extractionQuestions
        .filter(q => inputQuestionIds.includes(q.id))
        .map(q => ({
          field: q.id,
          headerName: q.title || 'Untitled',
          type: 'string',
          variant: 'ai' as const,
        }));
      const unselectedAi = extractionQuestions
        .filter(q => q.id !== question.id && !inputQuestionIds.includes(q.id))
        .map(q => ({
          field: q.id,
          headerName: q.title || 'Untitled',
          type: 'string',
          variant: 'ai' as const,
        }));

      return {
        selectedInputs: [...selectedAi, ...selectedFileFields],
        unselectedInputs: [...unselectedAi, ...unselectedFileFields],
      };
    }

    const gridApi = apiRef.current;
    // In report context, only AI inputs that are columns in the report.
    const questionIdsVisibleInReport = gridApi
      .getVisibleColumns()
      .filter(col => isUUIDV4(col.field))
      .map(col => col.field);

    // Also include file field columns visible in the report
    const fileFieldsVisibleInReport = new Set(
      gridApi
        .getVisibleColumns()
        .filter(col => !isUUIDV4(col.field))
        .map(col => col.field)
    );
    const visibleUnselectedFileFields = unselectedFileFields.filter(col =>
      fileFieldsVisibleInReport.has(col.field)
    );

    const { selectedAi, unselectedAi } = extractionQuestions.reduce<{
      selectedAi: CompatibleColumn[];
      unselectedAi: CompatibleColumn[];
    }>(
      (acc, q) => {
        const col: CompatibleColumn = {
          field: q.id,
          headerName: q.title || 'Untitled',
          type: 'string',
          variant: 'ai',
        };
        const isQuestion = q.id === question.id;
        const isSelected = inputQuestionIds.includes(q.id);
        const isQuestionAvailableAsInput = questionIdsVisibleInReport.includes(q.id);

        if (isQuestion) return acc;
        if (isSelected) acc.selectedAi.push(col);
        else if (isQuestionAvailableAsInput) acc.unselectedAi.push(col);

        return acc;
      },
      { selectedAi: [], unselectedAi: [] }
    );

    return {
      selectedInputs: [...selectedAi, ...selectedFileFields],
      unselectedInputs: [...unselectedAi, ...visibleUnselectedFileFields],
    };
  }, [
    allFileFieldValues,
    apiRef,
    extractionQuestions,
    inputFileFields,
    inputQuestionIds,
    question.id,
  ]);

  const handleSelectInput = (input: CompatibleColumn) => {
    if (input.variant === 'fileField') {
      const field = input.field;
      const isAlreadyInput = inputFileFields.includes(field);
      const newFileFields = isAlreadyInput
        ? inputFileFields.filter(f => f !== field)
        : [...inputFileFields, field];
      onChangeSelectedInputs({ inputFileFields: newFileFields, inputQuestionIds });
    } else {
      const isAlreadyInput = inputQuestionIds.includes(input.field);
      const newInputIds = isAlreadyInput
        ? inputQuestionIds.filter(id => id !== input.field)
        : uniq([...inputQuestionIds, input.field]);
      onChangeSelectedInputs({ inputFileFields, inputQuestionIds: newInputIds });
    }
  };

  useClickAway(popperRef, () => {
    onClose();
    setSearch('');
  });

  return (
    <Popper
      ref={popperRef}
      anchorEl={anchorEl}
      className={classes.popper}
      id={domElIdCreators.questionTypeMenuEl()}
      placement={placement}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
      {...rest}
    >
      <Paper className={classes.paper}>
        <div className={classes.searchContainer}>
          <SearchInput
            placeholder="Search columns..."
            search={search}
            setSearch={setSearch}
            style={{ minWidth: 'unset' }}
          />
        </div>
        <Divider />
        {/* Stop wheel event propagation to prevent parent column menu from closing when scrolling */}
        <div className={classes.scrollContainer} onWheel={e => e.stopPropagation()}>
          {/* MenuList provides the MenuListContext that v9 MenuItem requires. */}
          <MenuList>
            <SelectedInputMenuItems
              selectedInputs={selectedInputs}
              onToggleInput={handleSelectInput}
            />
            <Divider />
            <UnselectedInputMenuItems
              search={search}
              unselectedInputs={unselectedInputs}
              onToggleInput={input => {
                handleSelectInput(input);
                setSearch('');
              }}
            />
          </MenuList>
        </div>
      </Paper>
    </Popper>
  );
}

const useMenuItemStyles = makeStyles<{ showCheck: boolean }>()((theme, { showCheck }) => ({
  checkbox: { padding: 0 },
  hideIcon: {
    visibility: showCheck ? ('visible' as const) : ('hidden' as const),
  },
  inputIcon: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
    width: 20,
  },
  menuItem: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
  },
  propertiesText: {
    fontWeight: 600,
  },
}));

interface InputMenuItemsProps {
  onToggleInput: (input: CompatibleColumn) => void;
  search: string;
  unselectedInputs: CompatibleColumn[];
}

function UnselectedInputMenuItems({
  onToggleInput,
  search,
  unselectedInputs,
}: InputMenuItemsProps): JSX.Element {
  const { classes } = useMenuItemStyles({ showCheck: true });
  const searchedInputs = matchSorter(unselectedInputs, search, {
    keys: ['headerName' satisfies keyof CompatibleColumn],
  });

  if (!searchedInputs.length)
    return (
      <>
        <div className={classes.menuItem}>
          <Typography className={classes.propertiesText} variant="caption">
            Properties
          </Typography>
        </div>
        <div className={classes.menuItem}>
          <Typography align="center">
            No columns. Add a column to your report or adjust your search.
          </Typography>
        </div>
      </>
    );

  return (
    <>
      <div className={classes.menuItem}>
        <Typography className={classes.propertiesText} variant="caption">
          Properties
        </Typography>
      </div>
      {searchedInputs.map((input, index) => {
        return (
          <InputMenuItem
            key={index}
            input={input}
            isSelected={false}
            onToggleInput={onToggleInput}
          />
        );
      })}
    </>
  );
}

function SelectedInputMenuItems({
  onToggleInput,
  selectedInputs,
}: {
  onToggleInput: (input: CompatibleColumn) => void;
  selectedInputs: CompatibleColumn[];
}): JSX.Element {
  const { classes } = useMenuItemStyles({ showCheck: true });

  if (!selectedInputs.length)
    return (
      <div className={classes.menuItem}>
        <AlineCheckbox checked classes={{ root: classes.checkbox }} />
        <div className={classes.inputIcon}>
          <GenericPropertyIcon />
        </div>
        <Typography>File</Typography>
      </div>
    );

  return (
    <>
      {selectedInputs.map(input => {
        return (
          <InputMenuItem
            key={input.field}
            input={input}
            isSelected={true}
            onToggleInput={onToggleInput}
          />
        );
      })}
    </>
  );
}

interface InputMenuItemProps {
  input: CompatibleColumn;
  onToggleInput: (input: CompatibleColumn) => void;
  isSelected?: boolean;
  showCheckOverride?: boolean;
}

function InputMenuItem({
  input,
  isSelected,
  onToggleInput,
  showCheckOverride,
}: InputMenuItemProps): JSX.Element {
  const [showCheck, setShowCheck] = useState<boolean>(false);
  const { classes } = useMenuItemStyles({ showCheck });

  function CheckIcon(): JSX.Element {
    if (showCheckOverride || isSelected)
      return <AlineCheckbox checked classes={{ root: classes.checkbox }} />;

    return (
      <AlineCheckbox
        checked={false}
        className={classes.hideIcon}
        classes={{ root: classes.checkbox }}
      />
    );
  }

  return (
    <MenuItem
      className={classes.menuItem}
      onClick={() => onToggleInput(input)}
      onMouseLeave={() => setShowCheck(false)}
      onMouseOver={() => setShowCheck(true)}
    >
      <CheckIcon />
      <div className={classes.inputIcon}>
        {input.variant === 'fileField' ? <GenericPropertyIcon /> : <SmallAiSparkleIcon />}
      </div>
      <Typography noWrap>{input.headerName || 'Untitled'}</Typography>
    </MenuItem>
  );
}
